import type { BillingPeriod } from '../types/agent';
import { supabase } from './supabaseStorage';

/** OpenAI-style multimodal parts for OpenRouter (chat completions). */
export type AgentChatContentPart =
  | { type: 'text'; text: string }
  | { type: 'image_url'; image_url: { url: string } };

const agentApiUrl = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-api`;
const agentAutogenerateUrl = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-autogenerate`;
const falToolParseUrl = () => `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/fal-tool-parse`;

async function edgeAuthJsonHeaders(): Promise<HeadersInit | null> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!session?.access_token || !anon) return null;
  return {
    Authorization: `Bearer ${session.access_token}`,
    apikey: anon,
    'Content-Type': 'application/json',
  };
}

async function post(body: Record<string, unknown>) {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return { ok: false, status: 401, data: { error: 'Sign in required' } };
  }
  const res = await fetch(agentApiUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

async function postMultipart(form: FormData) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
  if (!session?.access_token || !anon) {
    return { ok: false, status: 401, data: { error: 'Sign in required' } };
  }
  const res = await fetch(agentApiUrl(), {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: anon,
    },
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  return { ok: res.ok, status: res.status, data };
}

/** Base64 payload for Edge Whisper (no data: prefix). */
export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const s = reader.result as string;
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export interface AgentInstanceFileRow {
  id: string;
  storage_path: string;
  filename: string;
  mime: string | null;
  bytes: number | null;
  created_at: string;
}

export async function agentOnboard(params: {
  appId?: string;
  agentTemplateId: string;
  onboardingAnswers: Record<string, string>;
  billingPeriod: BillingPeriod;
}) {
  return post({
    action: 'onboard',
    app_id: params.appId?.trim() || 'web',
    agent_template_id: params.agentTemplateId,
    onboarding_answers: params.onboardingAnswers,
    billing_period: params.billingPeriod,
  });
}

export async function agentRenew(params: { appId?: string; instanceId: string }) {
  return post({
    action: 'renew',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
  });
}

export async function agentChat(params: {
  appId?: string;
  instanceId: string;
  /** Plain text (optional if message_parts set). */
  message?: string;
  /** Multimodal user turn (OpenRouter / OpenAI-compatible). */
  message_parts?: AgentChatContentPart[];
}) {
  const body: Record<string, unknown> = {
    action: 'chat',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
    message: params.message ?? '',
  };
  if (params.message_parts?.length) {
    body.message_parts = params.message_parts;
  }
  return post(body);
}

/** SSE events from agent-api besides token deltas (tool rounds, web search progress). */
export type AgentChatStreamMeta =
  | { ready: true }
  | { phase: 'context'; status: 'built'; buildMs: number; useToolLoop: boolean }
  | { phase: 'tool'; tool: string; status: 'start' | 'done'; queryPreview?: string }
  | { phase: 'llm_round'; round: number; status: 'start' };

/** Server streams SSE (`text/event-stream`); persists messages after completion. */
export async function agentChatStream(params: {
  appId?: string;
  instanceId: string;
  message?: string;
  message_parts?: AgentChatContentPart[];
  onDelta: (chunk: string) => void;
  /** Tool/web-search phases, first-byte `ready`, etc. */
  onMeta?: (ev: AgentChatStreamMeta) => void;
}): Promise<{ ok: boolean; fullText?: string; error?: string; code?: string }> {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return { ok: false, error: 'Sign in required', code: 'auth' };
  }
  const body: Record<string, unknown> = {
    action: 'chat',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
    message: params.message ?? '',
    stream: true,
  };
  if (params.message_parts?.length) {
    body.message_parts = params.message_parts;
  }
  const res = await fetch(agentApiUrl(), {
    method: 'POST',
    cache: 'no-store',
    headers,
    body: JSON.stringify(body),
  });

  if (!res.ok) {
    const data = (await res.json().catch(() => ({}))) as { error?: string; code?: string };
    return {
      ok: false,
      error: data?.error || `Request failed (${res.status})`,
      code: typeof data?.code === 'string' ? data.code : undefined,
    };
  }

  const ct = res.headers.get('content-type') || '';
  if (!ct.includes('text/event-stream') || !res.body) {
    const data = (await res.json().catch(() => ({}))) as { reply?: string; error?: string; code?: string };
    if (data.reply) {
      params.onDelta(data.reply);
      return { ok: true, fullText: data.reply };
    }
    return {
      ok: false,
      error: data?.error || 'Unexpected response',
      code: typeof data?.code === 'string' ? data.code : undefined,
    };
  }

  /** Proxies often emit CRLF; `\n\n` alone never matches `\r\n\r\n`, so SSE blocks never parse. */
  const normalizeSseBuffer = (s: string) => s.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

  let fullText = '';
  const processSseBlock = (
    block: string
  ):
    | { done?: true; error?: string; code?: string; fullText?: string }
    | undefined => {
    for (const line of block.split('\n')) {
      const t = line.trim();
      if (!t.startsWith('data:')) continue;
      const raw = t.slice(5).trim();
      try {
        const parsed = JSON.parse(raw) as {
          delta?: string;
          error?: string;
          code?: string;
          done?: boolean;
          reply?: string;
          ready?: boolean;
          phase?: string;
        };
        if (typeof parsed.error === 'string') {
          return {
            error: parsed.error,
            code: typeof parsed.code === 'string' ? parsed.code : undefined,
          };
        }
        if (parsed.ready === true) {
          params.onMeta?.({ ready: true });
        }
        if (typeof parsed.phase === 'string') {
          params.onMeta?.(parsed as AgentChatStreamMeta);
        }
        if (typeof parsed.delta === 'string' && parsed.delta.length) {
          fullText += parsed.delta;
          params.onDelta(parsed.delta);
        }
        if (parsed.done) {
          return {
            done: true,
            fullText: typeof parsed.reply === 'string' ? parsed.reply : fullText,
          };
        }
      } catch {
        /* ignore malformed line */
      }
    }
  };

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  while (true) {
    const { done, value } = await reader.read();
    if (value) buffer += decoder.decode(value, { stream: true });
    if (done) {
      buffer += decoder.decode(new Uint8Array(), { stream: false });
      break;
    }
    buffer = normalizeSseBuffer(buffer);
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) >= 0) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const r = processSseBlock(block);
      if (r?.error) return { ok: false, error: r.error, code: r.code };
      if (r?.done) {
        const ft = r.fullText ?? fullText;
        return { ok: true, fullText: ft };
      }
    }
  }
  buffer = normalizeSseBuffer(buffer);
  let idx: number;
  while ((idx = buffer.indexOf('\n\n')) >= 0) {
    const block = buffer.slice(0, idx);
    buffer = buffer.slice(idx + 2);
    const r = processSseBlock(block);
    if (r?.error) return { ok: false, error: r.error, code: r.code };
    if (r?.done) {
      const ft = r.fullText ?? fullText;
      return { ok: true, fullText: ft };
    }
  }
  if (buffer.trim()) {
    const r = processSseBlock(buffer);
    if (r?.error) return { ok: false, error: r.error, code: r.code };
    if (r?.done) {
      const ft = r.fullText ?? fullText;
      return { ok: true, fullText: ft };
    }
  }
  return { ok: true, fullText };
}

function parseEdgeErrorBody(text: string): { data: unknown; errorMessage?: string } {
  if (!text?.trim()) {
    return { data: {} };
  }
  try {
    const data = JSON.parse(text) as Record<string, unknown>;
    const errorMessage =
      typeof data.error === 'string'
        ? data.error
        : typeof data.message === 'string'
          ? data.message
          : undefined;
    return { data, errorMessage };
  } catch {
    return { data: { raw: text }, errorMessage: text.slice(0, 300) };
  }
}

/** Admin: draft agent fields from app description (Edge; OpenRouter/OpenAI secrets — no client API key). */
export async function agentAutogenerate(params: {
  appDescription: string;
}) {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return { ok: false, status: 401, data: {}, errorMessage: 'Sign in required' };
  }
  const body: Record<string, unknown> = {
    app_description: params.appDescription,
  };
  const res = await fetch(agentAutogenerateUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });
  const text = await res.text();
  const { data, errorMessage } = parseEdgeErrorBody(text);
  return { ok: res.ok, status: res.status, data, errorMessage };
}

/** Admin: parse pasted fal.ai HTTP docs into central tool JSON (Edge LLM). */
export async function falToolParseDoc(params: {
  docText: string;
}) {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return { ok: false, status: 401, data: {}, errorMessage: 'Sign in required' };
  }
  const res = await fetch(falToolParseUrl(), {
    method: 'POST',
    headers,
    body: JSON.stringify({
      doc_text: params.docText,
    }),
  });
  const text = await res.text();
  const { data, errorMessage } = parseEdgeErrorBody(text);
  return { ok: res.ok, status: res.status, data, errorMessage };
}

/** Admin: OpenRouter → cover prompt, fal `openai/gpt-image-2` 16:9 (Edge; OPENROUTER_API_KEY + FAL_KEY). */
export async function agentGenerateCoverImage(params: {
  agentTemplateId: string;
  /** Optional; server builds from agent name/description if empty. */
  prompt?: string;
}): Promise<{
  ok: boolean;
  status: number;
  publicUrl?: string;
  imagePrompt?: string;
  errorMessage?: string;
}> {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return { ok: false, status: 401, publicUrl: undefined, errorMessage: 'Sign in required' };
  }
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-cover-image`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      agent_template_id: params.agentTemplateId,
      prompt: (params.prompt ?? '').trim(),
    }),
  });
  const text = await res.text();
  let parsed: Record<string, unknown> = {};
  try {
    parsed = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    parsed = { error: text?.slice(0, 300) };
  }
  const errorMessage =
    typeof parsed.error === 'string'
      ? parsed.error
      : typeof parsed.message === 'string'
        ? parsed.message
        : undefined;
  const ok = res.ok && parsed.success === true;
  const publicUrl = typeof parsed.public_url === 'string' ? parsed.public_url : undefined;
  const imagePrompt = typeof parsed.image_prompt === 'string' ? parsed.image_prompt : undefined;
  return { ok, status: res.status, publicUrl, imagePrompt, errorMessage };
}

/** Admin: 1:1 role-based avatar — OpenRouter → prompt, fal `openai/gpt-image-2` (Edge; OPENROUTER_API_KEY + FAL_KEY). */
export async function agentGenerateAvatarImage(params: {
  agentTemplateId: string;
  roleKeyword: string;
}): Promise<{
  ok: boolean;
  status: number;
  publicUrl?: string;
  imagePrompt?: string;
  errorMessage?: string;
}> {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return { ok: false, status: 401, publicUrl: undefined, errorMessage: 'Sign in required' };
  }
  const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/agent-avatar-image`, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      agent_template_id: params.agentTemplateId,
      role_keyword: params.roleKeyword.trim(),
    }),
  });
  const text = await res.text();
  let parsed: Record<string, unknown> = {};
  try {
    parsed = text ? (JSON.parse(text) as Record<string, unknown>) : {};
  } catch {
    parsed = { error: text?.slice(0, 300) };
  }
  const errorMessage =
    typeof parsed.error === 'string'
      ? parsed.error
      : typeof parsed.message === 'string'
        ? parsed.message
        : undefined;
  const ok = res.ok && parsed.success === true;
  const publicUrl = typeof parsed.public_url === 'string' ? parsed.public_url : undefined;
  const imagePrompt = typeof parsed.image_prompt === 'string' ? parsed.image_prompt : undefined;
  return { ok, status: res.status, publicUrl, imagePrompt, errorMessage };
}

/** Rebuild template KB embeddings (staff; JWT + template ownership / superadmin). */
export async function agentReindexKb(params: {
  appId?: string;
  agentTemplateId: string;
}) {
  return post({
    action: 'reindex_kb',
    app_id: params.appId?.trim() || 'web',
    agent_template_id: params.agentTemplateId,
  });
}

/** Admin: generate knowledge-base paste text from agent fields (Edge LLM). */
export async function agentSynthesizeKbDraft(params: {
  appId?: string;
  agentName?: string;
  publicDescription?: string;
  systemInstructions?: string;
  guardrails?: string;
}) {
  return post({
    action: 'synthesize_kb_draft',
    app_id: params.appId?.trim() || 'web',
    agent_name: params.agentName ?? '',
    public_description: params.publicDescription ?? '',
    system_instructions: params.systemInstructions ?? '',
    guardrails: params.guardrails ?? '',
  });
}

/** Admin: expand system instructions and/or guardrails (Edge LLM). */
export async function agentExpandAgentSystem(params: {
  appId?: string;
  expandTarget: 'instructions' | 'guardrails' | 'both';
  systemInstructions?: string;
  guardrails?: string;
  /** When true, expanded text includes file-attachment behavior (matches Features tab). */
  fileUploadsEnabled?: boolean;
  /** When true, expanded text includes voice/TTS behavior (matches Features tab). */
  voiceEnabled?: boolean;
}) {
  return post({
    action: 'expand_agent_system',
    app_id: params.appId?.trim() || 'web',
    expand_target: params.expandTarget,
    system_instructions: params.systemInstructions ?? '',
    guardrails: params.guardrails ?? '',
    file_uploads_enabled: params.fileUploadsEnabled === true,
    voice_enabled: params.voiceEnabled === true,
  });
}

export async function agentTranscribe(params: {
  appId?: string;
  audioBase64: string;
  mimeType?: string;
  /** BCP-47 hint; omit for auto-detect (Whisper). */
  language?: string;
  /** When set, Edge rejects if instance is banned (agent chat). */
  instanceId?: string;
}) {
  const body: Record<string, unknown> = {
    action: 'transcribe',
    app_id: params.appId?.trim() || 'web',
    audio_base64: params.audioBase64,
    mime_type: params.mimeType ?? 'audio/webm',
  };
  if (params.language) body.language = params.language;
  if (params.instanceId) body.instance_id = params.instanceId;
  return post(body);
}

export async function agentTts(params: { appId?: string; text: string }) {
  return post({
    action: 'tts',
    app_id: params.appId?.trim() || 'web',
    text: params.text,
  });
}

/** Proxies OpenAI Speech SSE (`speech.audio.delta` PCM); use with `playOpenAiSpeechSsePcm`. */
export async function agentTtsStream(params: {
  appId?: string;
  text: string;
  signal?: AbortSignal;
  /** When set, Edge rejects if instance is banned (agent chat). */
  instanceId?: string;
}): Promise<Response> {
  const headers = await edgeAuthJsonHeaders();
  if (!headers) {
    return new Response(JSON.stringify({ error: 'Sign in required' }), { status: 401 });
  }
  const body: Record<string, unknown> = {
    action: 'tts_stream',
    app_id: params.appId?.trim() || 'web',
    text: params.text,
  };
  if (params.instanceId) body.instance_id = params.instanceId;
  return fetch(agentApiUrl(), {
    method: 'POST',
    cache: 'no-store',
    headers,
    body: JSON.stringify(body),
    signal: params.signal,
  });
}

export async function agentClearMessages(params: { appId?: string; instanceId: string }) {
  return post({
    action: 'clear_messages',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
  });
}

export async function agentDeleteInstance(params: { appId?: string; instanceId: string }) {
  return post({
    action: 'delete_my_instance',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
  });
}

export async function agentListFiles(params: { appId?: string; instanceId: string }) {
  return post({
    action: 'list_files',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
  });
}

export async function agentDeleteFile(params: {
  appId?: string;
  instanceId: string;
  fileId: string;
}) {
  return post({
    action: 'delete_file',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
    file_id: params.fileId,
  });
}

export async function agentUploadFile(params: {
  appId?: string;
  instanceId: string;
  file: File;
}) {
  const form = new FormData();
  form.append('action', 'upload_file');
  form.append('app_id', params.appId?.trim() || 'web');
  form.append('instance_id', params.instanceId);
  form.append('file', params.file);
  return postMultipart(form);
}

export async function agentReinstateAfterBan(params: { appId?: string; instanceId: string }) {
  return post({
    action: 'reinstate_after_ban',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
  });
}

export type AdminAgentInstanceRow = {
  id: string;
  hy_user_id: string;
  /** From `users.hy_name` when the user row exists. */
  hy_name?: string | null;
  agent_template_id: string;
  onboarding_answers: Record<string, string>;
  custom_instructions?: string | null;
  status: string;
  created_at: string;
  updated_at: string;
  banned_at?: string | null;
  banned_reason?: string | null;
  banned_by_hy_user_id?: string | null;
  template_name?: string | null;
  message_count: number;
  file_count: number;
  last_message_at?: string | null;
};

export async function adminListAgentInstances(params: {
  appId?: string;
  offset?: number;
  limit?: number;
}) {
  return post({
    action: 'admin_list_agent_instances',
    app_id: params.appId?.trim() || 'web',
    offset: params.offset ?? 0,
    limit: params.limit ?? 50,
  });
}

export async function adminSetInstanceBanned(params: {
  appId?: string;
  instanceId: string;
  banned: boolean;
  reason?: string;
}) {
  return post({
    action: 'admin_set_instance_banned',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
    banned: params.banned,
    reason: params.reason ?? '',
  });
}

export async function adminInstanceHealthCheck(params: {
  appId?: string;
  instanceId: string;
}) {
  return post({
    action: 'admin_instance_health_check',
    app_id: params.appId?.trim() || 'web',
    instance_id: params.instanceId,
  });
}
